import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { WeeklyTimesheet, WeeklyTimesheetEntry } from '../types/timesheet.ts';
import { Employee } from '../types/employee.ts';
import { Contractor } from '../types/contractor.ts';
import { calculateBurdenedRate, calculateContractorRates } from '../utils/rateCalculations.ts';
import { FileText, ArrowRight, CheckCircle, AlertCircle, User, Calendar, Clock, Package, Loader } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { invoiceNumberingService } from '../services/invoiceNumberingService.ts';

interface ProcessedTimesheetData {
  'INV #': string;
  'EMPLOYEE': string;
  'PAY TYPE': string;
  'Activity Code': string;
  'Activity Description': string;
  'JOB NAME': string;
  'JOB NUMBER': string;
  'WEEK ENDING': string;
  'HOURS': number;
  'BURDENED RATE': number;
  'PAYMENT_TERMS'?: string;
}

interface ContractorGroup {
  contractorPrefix: string;
  contractorName: string;
  invoiceNumber: string;
  paymentTerms?: string;
  employees: {
    [employeeName: string]: {
      regularHours: number;
      overtimeHours: number;
      regularRate: number;
      overtimeRate: number;
      activityCode?: string;
      activityDescription?: string;
    }
  };
}

interface TimesheetProcessingResult {
  timesheet: WeeklyTimesheet;
  contractorGroups: ContractorGroup[];
  error?: string;
}

const BatchTimesheetToInvoiceConverter: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [timesheets, setTimesheets] = useState<WeeklyTimesheet[]>([]);
  const [employees, setEmployees] = useState<{ [id: string]: Employee }>({});
  const [contractors, setContractors] = useState<{ [id: string]: Contractor }>({});
  const [processingResults, setProcessingResults] = useState<TimesheetProcessingResult[]>([]);
  const [nextInvoiceNumbers, setNextInvoiceNumbers] = useState<{[key: string]: string}>({});

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (ids) {
      const timesheetIds = ids.split(',');
      loadTimesheetsAndEmployees(timesheetIds);
    } else {
      setError('No timesheet IDs provided');
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    if (timesheets.length > 0 && Object.keys(employees).length > 0) {
      loadContractors();
    }
  }, [timesheets, employees]);

  useEffect(() => {
    if (timesheets.length > 0 && Object.keys(employees).length > 0 && Object.keys(contractors).length > 0) {
      processTimesheetsForInvoicing();
    }
  }, [timesheets, employees, contractors]);

  useEffect(() => {
    const loadNextInvoiceNumbers = async () => {
      try {
        const numbers = await invoiceNumberingService.getNextInvoiceNumbers();
        setNextInvoiceNumbers(numbers);
      } catch (error) {
        console.error('Error loading next invoice numbers:', error);
      }
    };
    
    loadNextInvoiceNumbers();
  }, []);

  const loadContractors = async () => {
    try {
      const contractorsRef = collection(db, 'contractors');
      const q = query(contractorsRef, orderBy('companyName'));
      const snapshot = await getDocs(q);
      
      const contractorData: { [id: string]: Contractor } = {};
      snapshot.docs.forEach(doc => {
        contractorData[doc.id] = {
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          licenseExpires: doc.data().licenseExpires?.toDate(),
          insuranceExpires: doc.data().insuranceExpires?.toDate(),
          bondExpires: doc.data().bondExpires?.toDate(),
          lastLoginAt: doc.data().lastLoginAt?.toDate()
        } as Contractor;
      });
      
      setContractors(contractorData);
    } catch (err) {
      console.error('Error loading contractors:', err);
      // Don't set error as this is not critical, but we still need to process
      setContractors({});
    }
  };

  const loadTimesheetsAndEmployees = async (timesheetIds: string[]) => {
    try {
      setLoading(true);
      
      const loadedTimesheets: WeeklyTimesheet[] = [];
      const allEmployeeIds = new Set<string>();
      
      for (const id of timesheetIds) {
        try {
          const timesheetRef = doc(db, 'weeklyTimesheets', id);
          const timesheetSnap = await getDoc(timesheetRef);
          
          if (timesheetSnap.exists()) {
            const timesheetData = {
              id: timesheetSnap.id,
              ...timesheetSnap.data(),
              weekEnding: timesheetSnap.data()?.weekEnding?.toDate() || new Date(),
              createdAt: timesheetSnap.data()?.createdAt?.toDate() || new Date(),
              updatedAt: timesheetSnap.data()?.updatedAt?.toDate() || new Date()
            } as WeeklyTimesheet;
            
            loadedTimesheets.push(timesheetData);
            
            timesheetData.entries.forEach(entry => {
              if (entry.employeeId) {
                allEmployeeIds.add(entry.employeeId);
              }
            });
          }
        } catch (err) {
          console.error(`Error loading timesheet ${id}:`, err);
        }
      }
      
      setTimesheets(loadedTimesheets);
      
      if (allEmployeeIds.size > 0) {
        console.log('Loading employees with IDs:', Array.from(allEmployeeIds));
        const employeeData: { [id: string]: Employee } = {};
        
        for (const empId of allEmployeeIds) {
          try {
            console.log(`Loading employee ${empId}...`);
            const empRef = doc(db, 'employees', empId);
            const empSnap = await getDoc(empRef);
            
            if (empSnap.exists()) {
              const empData = empSnap.data();
              console.log(`Employee ${empId} data:`, empData);
              employeeData[empId] = {
                id: empSnap.id,
                ...empData,
                createdAt: empData?.createdAt?.toDate() || new Date(),
                updatedAt: empData?.updatedAt?.toDate() || new Date(),
                hireDate: empData?.hireDate?.toDate(),
                terminationDate: empData?.terminationDate?.toDate(),
                licenseExpiration: empData?.licenseExpiration?.toDate(),
              } as Employee;
              console.log(`Loaded employee:`, employeeData[empId]);
            } else {
              console.error(`Employee ${empId} not found in database`);
            }
          } catch (err) {
            console.error(`Error loading employee ${empId}:`, err);
          }
        }
        
        console.log('Final employee data:', employeeData);
        setEmployees(employeeData);
      }
    } catch (err) {
      console.error('Error loading timesheets:', err);
      setError('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  };

  const processTimesheetsForInvoicing = async () => {
    const results: TimesheetProcessingResult[] = [];
    
    // Get all dynamic contractor prefixes from the database
    const allPrefixes = new Set<string>();
    allPrefixes.add('CEC'); // Always include CEC as default
    
    // Add prefixes from all contractors
    Object.values(contractors).forEach(contractor => {
      if (contractor.invoicePrefix && contractor.invoicePrefix.trim()) {
        allPrefixes.add(contractor.invoicePrefix.trim().toUpperCase());
      }
    });
    
    const companies = Array.from(allPrefixes).sort();
    console.log('Dynamic contractor prefixes found:', companies);
    
    // Get the starting invoice numbers for all companies
    const startingNumbers: { [prefix: string]: number } = {};
    
    for (const company of companies) {
      try {
        const nextNumber = await invoiceNumberingService.peekNextInvoiceNumber(company);
        startingNumbers[company] = parseInt(company === 'CEC' ? nextNumber : nextNumber.split('-')[1]);
      } catch (error) {
        console.error(`Error getting starting number for ${company}:`, error);
        startingNumbers[company] = 1001;
      }
    }

    // Track how many invoices we've assigned per company (dynamic initialization)
    const invoiceCounters: { [prefix: string]: number } = {};
    companies.forEach(company => {
      invoiceCounters[company] = 0;
    });
    
    for (const timesheet of timesheets) {
      try {
        const contractorGroups = await processTimesheetForInvoicing(timesheet, startingNumbers, invoiceCounters);
        results.push({
          timesheet,
          contractorGroups
        });
      } catch (err) {
        results.push({
          timesheet,
          contractorGroups: [],
          error: `Failed to process: ${err}`
        });
      }
    }
    
    setProcessingResults(results);
  };

  const processTimesheetForInvoicing = async (
    timesheet: WeeklyTimesheet, 
    startingNumbers: { [prefix: string]: number },
    invoiceCounters: { [prefix: string]: number }
  ): Promise<ContractorGroup[]> => {
    console.log('Processing timesheet for batch invoicing:', timesheet.jobName);
    console.log('Timesheet contractor info:', { contractorId: timesheet.contractorId, contractorName: timesheet.contractorName });
    
    const groups: { [key: string]: ContractorGroup } = {};

    const contractorPrefixes = new Set<string>();
    
    // Check if timesheet has a pre-assigned contractor
    let timesheetContractorId: string | null = null;
    let timesheetContractor: Contractor | null = null;
    
    if (timesheet.contractorId && contractors[timesheet.contractorId]) {
      timesheetContractorId = timesheet.contractorId;
      timesheetContractor = contractors[timesheet.contractorId];
      console.log('Timesheet has pre-assigned contractor:', timesheetContractor.companyName);
    }
    
    timesheet.entries.forEach(entry => {
      console.log(`Processing entry for employee: ${entry.employeeName}, ID: ${entry.employeeId}`);
      if (!entry.employeeId || !employees[entry.employeeId]) {
        console.error(`Employee not found: ${entry.employeeName} (ID: ${entry.employeeId})`);
        console.log('Available employees:', Object.keys(employees));
        return;
      }

      const employee = employees[entry.employeeId];
      console.log(`Found employee in database:`, employee);
      const totalHours = Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
      
      if (totalHours === 0) return;

      // Determine contractor prefix - if timesheet has a contractor assigned, use it for ALL employees
      let contractorPrefix = '';
      
      if (timesheetContractor) {
        // Use the contractor's invoicePrefix if it has one, otherwise fallback to legacy logic
        if (timesheetContractor.invoicePrefix) {
          contractorPrefix = timesheetContractor.invoicePrefix;
          const employeeName = employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
          console.log(`Using contractor's invoicePrefix "${contractorPrefix}" for employee ${employeeName}`);
        } else {
          // Legacy fallback based on company name for contractors without invoicePrefix set
          const contractorName = timesheetContractor.companyName.toLowerCase();
          if (contractorName.includes('btc') || contractorName.includes('build.test.connect') || contractorName.includes('build test connect')) {
            contractorPrefix = 'BTC';
          } else if (contractorName.includes('reliable') || contractorName.includes('rel')) {
            contractorPrefix = 'REL';
          } else if (contractorName.includes('cec') || contractorName.includes('commonwealth')) {
            contractorPrefix = ''; // CEC uses no prefix
          } else {
            contractorPrefix = ''; // Default to CEC (no prefix)
          }
          const employeeName = employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
          console.log(`Using timesheet contractor ${timesheetContractor.companyName} -> ${contractorPrefix || 'CEC (no prefix)'} for employee ${employeeName} (legacy fallback)`);
        }
      } else {
        // Fallback: determine contractor based on employee rates
        // Support both new rates structure and legacy individual rate fields
        const employeeName = employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
        const btcRate = employee.rates?.btc || employee.btcRate;
        const reliableRate = employee.rates?.reliable || employee.reliableRate;
        const cecRate = employee.rates?.cec || employee.cecRate;
        
        if (btcRate && btcRate > 0) {
          contractorPrefix = 'BTC';
          console.log(`Employee ${employeeName} assigned to BTC by rate (rate: ${btcRate})`);
        } else if (reliableRate && reliableRate > 0) {
          contractorPrefix = 'REL';
          console.log(`Employee ${employeeName} assigned to REL by rate (rate: ${reliableRate})`);
        } else {
          contractorPrefix = ''; // CEC uses no prefix
          console.log(`Employee ${employeeName} assigned to CEC by rate (rate: ${cecRate || employee.hourlyWage})`);
        }
      }

      // Normalize contractor prefix for internal use
      const normalizedPrefix = contractorPrefix || 'CEC';
      contractorPrefixes.add(normalizedPrefix);
    });

    // Assign consecutive invoice numbers without reserving
    const prefixInvoiceMap: { [prefix: string]: string } = {};
    for (const prefix of contractorPrefixes) {
      // Ensure we have starting numbers and counters for this prefix
      if (!(prefix in startingNumbers)) {
        console.warn(`Starting number not found for prefix ${prefix}, defaulting to 1001`);
        startingNumbers[prefix] = 1001;
      }
      if (!(prefix in invoiceCounters)) {
        console.warn(`Invoice counter not found for prefix ${prefix}, defaulting to 0`);
        invoiceCounters[prefix] = 0;
      }
      
      const invoiceNumber = startingNumbers[prefix] + invoiceCounters[prefix];
      prefixInvoiceMap[prefix] = invoiceNumber.toString();
      invoiceCounters[prefix]++; // Increment for next timesheet
    }

    timesheet.entries.forEach(entry => {
      if (!entry.employeeId || !employees[entry.employeeId]) {
        return;
      }

      const employee = employees[entry.employeeId];
      const totalHours = Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
      
      if (totalHours === 0) return;

      // Use same logic as above to determine contractor prefix and details
      let contractorPrefix = '';
      let contractorName = 'CEC Facilities Services';
      let regularRate = employee.hourlyWage || 0;
      let contractorForRates: Contractor | null = null;

      if (timesheetContractor) {
        contractorForRates = timesheetContractor;
        contractorName = timesheetContractor.companyName;
        
        // Use contractor's invoicePrefix if available, otherwise legacy logic
        if (timesheetContractor.invoicePrefix) {
          contractorPrefix = timesheetContractor.invoicePrefix;
        } else {
          // Legacy prefix determination from company name
          const contractorNameLower = timesheetContractor.companyName.toLowerCase();
          if (contractorNameLower.includes('btc') || contractorNameLower.includes('build.test.connect') || contractorNameLower.includes('build test connect')) {
            contractorPrefix = 'BTC';
          } else if (contractorNameLower.includes('reliable') || contractorNameLower.includes('rel')) {
            contractorPrefix = 'REL';
          } else if (contractorNameLower.includes('cec') || contractorNameLower.includes('commonwealth')) {
            contractorPrefix = ''; // CEC uses no prefix
          } else {
            contractorPrefix = ''; // Default to CEC (no prefix)
          }
        }
        
        console.log(`Using timesheet contractor ${contractorForRates.companyName} (${contractorPrefix || 'no prefix'}) for rate calculation`);
      } else {
        // Fallback: determine contractor based on employee rates
        // Support both new rates structure and legacy individual rate fields
        const employeeName = employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
        console.log(`Employee ${employeeName} rates for fallback:`, employee.rates);
        console.log(`Employee ${employeeName} legacy rates:`, {reliableRate: employee.reliableRate, cecRate: employee.cecRate, btcRate: employee.btcRate});
        
        const btcRate = employee.rates?.btc || employee.btcRate;
        const reliableRate = employee.rates?.reliable || employee.reliableRate;
        const cecRate = employee.rates?.cec || employee.cecRate;
        
        // Fallback: determine contractor based on employee rates and find matching contractor
        const contractorEntries = Object.entries(contractors);
        let matchedContractor: Contractor | null = null;
        
        if (btcRate && btcRate > 0) {
          contractorPrefix = 'BTC';
          contractorName = 'Build.Test.Connect';
          regularRate = btcRate;
          matchedContractor = contractorEntries.find(([_, c]) => c.companyName.toLowerCase().includes('btc') || c.companyName.toLowerCase().includes('build'))?.[1] || null;
          console.log(`Fallback: Employee ${employeeName} assigned to BTC with rate ${regularRate}`);
        } else if (reliableRate && reliableRate > 0) {
          contractorPrefix = 'REL';
          contractorName = 'Reliable Contractors';
          regularRate = reliableRate;
          matchedContractor = contractorEntries.find(([_, c]) => c.companyName.toLowerCase().includes('reliable'))?.[1] || null;
          console.log(`Fallback: Employee ${employeeName} assigned to REL with rate ${regularRate}`);
        } else {
          contractorPrefix = ''; // CEC uses no prefix
          contractorName = 'CEC Facilities Services';
          regularRate = cecRate || employee.hourlyWage || 0;
          matchedContractor = contractorEntries.find(([_, c]) => c.companyName.toLowerCase().includes('cec') || c.companyName.toLowerCase().includes('commonwealth'))?.[1] || null;
          console.log(`Fallback: Employee ${employeeName} assigned to CEC with rate ${regularRate}`);
        }
        
        // Use the matched contractor for enhanced rate calculation
        contractorForRates = matchedContractor;
      }

      // Use enhanced rate calculation if contractor configuration is available
      if (contractorForRates) {
        const contractorConfig = {
          markupType: contractorForRates.billing.markupType,
          markupValue: contractorForRates.billing.markupValue
        };

        // Prepare rate inputs for calculation
        const rateInputs = {
          hourlyWage: employee.hourlyWage || 0,
          fica: employee.fica || 0,
          insurance: employee.insurance || 0,
          vacation: employee.vacation || 0,
          carFuel: employee.carFuel || 0,
          markup: employee.markup || 0,
          customRateAdjustments: employee.customRateAdjustments
        };

        // Calculate enhanced burdened rate
        regularRate = calculateBurdenedRate(rateInputs, contractorConfig);
        
        const employeeName = employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
        console.log(`Enhanced rate calculation for ${employeeName} with ${contractorForRates.companyName}: $${regularRate.toFixed(2)} (markup: ${contractorConfig.markupType} ${contractorConfig.markupValue})`);
      } else {
        console.log(`No contractor configuration found for ${contractorPrefix}, using base rate: $${regularRate.toFixed(2)}`);
      }

      // Normalize prefix for grouping
      const groupKey = contractorPrefix || 'CEC';
      
      // Helper function to format payment terms
      const formatPaymentTerms = (contractor: Contractor | null): string => {
        const terms = contractor?.billing?.paymentTerms;
        switch (terms) {
          case 'net15': return 'Net 15 Days';
          case 'net30': return 'Net 30 Days';
          case 'net45': return 'Net 45 Days';
          case 'immediate': return 'Due Immediately';
          case 'weekly': return 'Weekly';
          case 'biweekly': return 'Bi-weekly';
          default: return 'Net 30';
        }
      };
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          contractorPrefix,
          contractorName,
          invoiceNumber: prefixInvoiceMap[groupKey],
          paymentTerms: formatPaymentTerms(contractorForRates),
          employees: {}
        };
      }

      // Use actual regular/overtime hours from timesheet if available
      let regularHours = 0;
      let overtimeHours = 0;
      
      if (entry.regularHours && entry.overtimeHours) {
        // Sum up the actual regular and overtime hours from each day
        regularHours = Object.values(entry.regularHours).reduce((sum, hours) => sum + hours, 0);
        overtimeHours = Object.values(entry.overtimeHours).reduce((sum, hours) => sum + hours, 0);
      } else {
        // Fallback for legacy timesheets: use total hours as regular hours (no automatic OT calculation)
        regularHours = totalHours;
        overtimeHours = 0;
        
        console.log(`Legacy timesheet detected for ${entry.employeeName}: All ${totalHours} hours treated as regular`);
      }

      groups[groupKey].employees[entry.employeeName] = {
        regularHours,
        overtimeHours,
        regularRate,
        overtimeRate: regularRate * 1.5,
        activityCode: entry.activityCode,
        activityDescription: entry.activityDescription
      };
    });

    return Object.values(groups);
  };

  const convertToInvoiceFormat = (): ProcessedTimesheetData[] => {
    if (processingResults.length === 0) return [];

    const invoiceData: ProcessedTimesheetData[] = [];

    processingResults.forEach(result => {
      if (result.error) return;

      const { timesheet, contractorGroups } = result;
      const weekEndingStr = timesheet.weekEnding.toISOString().split('T')[0];

      contractorGroups.forEach(group => {
        Object.entries(group.employees).forEach(([employeeName, empData]) => {
          if (empData.regularHours > 0) {
            const finalInvoiceNumber = (!group.contractorPrefix || group.contractorPrefix === 'CEC') ? group.invoiceNumber : `${group.contractorPrefix}-${group.invoiceNumber}`;
            invoiceData.push({
              'INV #': finalInvoiceNumber,
              'EMPLOYEE': employeeName,
              'PAY TYPE': 'Regular',
              'Activity Code': empData.activityCode || '',
              'Activity Description': empData.activityDescription || '',
              'JOB NAME': timesheet.jobName,
              'JOB NUMBER': timesheet.jobNumber || '',
              'WEEK ENDING': weekEndingStr,
              'HOURS': empData.regularHours,
              'BURDENED RATE': empData.regularRate,
              'PAYMENT_TERMS': group.paymentTerms
            });
          }

          if (empData.overtimeHours > 0) {
            invoiceData.push({
              'INV #': (!group.contractorPrefix || group.contractorPrefix === 'CEC') ? group.invoiceNumber : `${group.contractorPrefix}-${group.invoiceNumber}`,
              'EMPLOYEE': employeeName,
              'PAY TYPE': 'Overtime',
              'Activity Code': empData.activityCode || '',
              'Activity Description': empData.activityDescription || '',
              'JOB NAME': timesheet.jobName,
              'JOB NUMBER': timesheet.jobNumber || '',
              'WEEK ENDING': weekEndingStr,
              'HOURS': empData.overtimeHours,
              'BURDENED RATE': empData.overtimeRate,
              'PAYMENT_TERMS': group.paymentTerms
            });
          }
        });
      });
    });

    return invoiceData;
  };

  const handleBatchConvertToInvoices = async () => {
    if (!currentUser || processingResults.length === 0) return;

    try {
      setConverting(true);
      
      const invoiceData = convertToInvoiceFormat();
      
      if (invoiceData.length === 0) {
        setError('No valid invoice data to convert');
        return;
      }

      localStorage.setItem('processedTimesheetData', JSON.stringify(invoiceData));
      
      for (const result of processingResults) {
        if (!result.error) {
          await updateDoc(doc(db, 'weeklyTimesheets', result.timesheet.id), {
            status: 'processed_for_invoice',
            processedForInvoice: true,
            invoiceNumbers: result.contractorGroups.map(g => g.invoiceNumber),
            updatedAt: new Date()
          });
        }
      }

      setSuccess(`Successfully converted ${processingResults.filter(r => !r.error).length} timesheets to invoice format!`);
      
      setTimeout(() => {
        navigate('/invoices/excel-generator');
      }, 2000);

    } catch (err) {
      console.error('Error converting to invoices:', err);
      setError('Failed to convert timesheets to invoice format');
    } finally {
      setConverting(false);
    }
  };

  const getTotalHours = (entry: WeeklyTimesheetEntry): number => {
    return Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (timesheets.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No timesheets found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The timesheets you're trying to convert could not be found.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/timesheets')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Back to Timesheets
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Package className="h-8 w-8 mr-3 text-blue-600" />
              Batch Convert to Invoices
            </h1>
            <p className="text-gray-600 mt-1">
              Convert {timesheets.length} timesheets to invoice format for processing
            </p>
          </div>
          <button
            onClick={() => navigate('/timesheets')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Back to Timesheets
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">×</button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-6 shadow-md">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-blue-800">Batch Processing Summary</h3>
            <p className="text-blue-700 mt-1">
              Processing {timesheets.length} timesheets. Each job will generate separate invoices based on contractor assignments.
            </p>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <div className="bg-white rounded px-2 py-1">
                <span className="font-medium text-gray-600">Next CEC:</span> 
                <span className="ml-1 text-gray-900">{nextInvoiceNumbers.CEC || 'Loading...'}</span>
              </div>
              <div className="bg-white rounded px-2 py-1">
                <span className="font-medium text-gray-600">Next REL:</span> 
                <span className="ml-1 text-gray-900">{nextInvoiceNumbers.REL || 'Loading...'}</span>
              </div>
              <div className="bg-white rounded px-2 py-1">
                <span className="font-medium text-gray-600">Next BTC:</span> 
                <span className="ml-1 text-gray-900">{nextInvoiceNumbers.BTC || 'Loading...'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {processingResults.map((result, index) => (
          <div key={result.timesheet.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                {result.timesheet.jobName}
                {result.timesheet.jobNumber && (
                  <span className="ml-2 text-sm text-gray-600">#{result.timesheet.jobNumber}</span>
                )}
              </h3>
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="h-4 w-4 mr-1" />
                Week ending: {result.timesheet.weekEnding.toLocaleDateString()}
              </div>
            </div>

            {result.error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                  <span className="text-red-700 font-medium">Processing Error</span>
                </div>
                <p className="text-red-600 text-sm mt-1">{result.error}</p>
              </div>
            ) : (
              <div>
                <div className="mb-4">
                  <h4 className="text-md font-medium text-gray-800 mb-2">Employee Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {result.timesheet.entries.map((entry, entryIndex) => {
                      const totalHours = getTotalHours(entry);
                      const employee = entry.employeeId ? employees[entry.employeeId] : null;
                      return (
                        <div key={entryIndex} className="bg-gray-50 p-2 rounded text-sm">
                          <div className="font-medium">{entry.employeeName}</div>
                          <div className="text-gray-600 flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {totalHours.toFixed(1)}h
                            {!employee && <span className="ml-1 text-yellow-600">⚠️</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {result.contractorGroups.length > 0 && (
                  <div>
                    <h4 className="text-md font-medium text-gray-800 mb-2">
                      Invoice Preview ({result.contractorGroups.length} invoice{result.contractorGroups.length > 1 ? 's' : ''})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {result.contractorGroups.map((group, groupIndex) => (
                        <div key={groupIndex} className="border rounded-lg p-3 bg-gray-50">
                          <div className="font-medium text-sm text-blue-900">
                            Invoice #{group.invoiceNumber} - {group.contractorName}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {Object.keys(group.employees).length} employee{Object.keys(group.employees).length > 1 ? 's' : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-8">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Ready to Convert</h3>
            <p className="text-gray-600">
              This will convert all valid timesheets to invoice format and redirect you to the invoice generator.
            </p>
            <div className="mt-2 text-sm text-gray-600">
              {processingResults.filter(r => !r.error).length} of {processingResults.length} timesheets ready for conversion
            </div>
          </div>
          <button
            onClick={handleBatchConvertToInvoices}
            disabled={converting || processingResults.filter(r => !r.error).length === 0}
            className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {converting ? (
              <>
                <Loader className="animate-spin h-4 w-4 mr-2" />
                Converting...
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4 mr-2" />
                Convert All to Invoices
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchTimesheetToInvoiceConverter;